import { faker } from "@faker-js/faker";
import { GenderApiValues } from "@utils/constants";
import { getGenderValue, setGenderValue } from "@utils/helpers";
import { formatDateYYYY_MM_DD } from "@utils/utils";
import { format } from "date-fns";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button, SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  UpdateReportClientDetailsMutationVariables,
  useAddReportClientDetailsMutation,
  useUpdateReportClientDetailsMutation,
} from "@api/graphql/types-and-hooks";
import { ContentRow } from "@components/ContentRow/ContentRow";
import { DevOnlyWrapper } from "@components/DevOnlyWrapper/DevOnlyWrapper";
import { DoubleColumnContainer, FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { EvaluationSubRoute, getNextRoute } from "@pages/EvaluationDetails/EvaluationDetails";
import { FooterButtonRow } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/FooterButtonRow";
import { useReport } from "@providers/ReportProvider";

interface EvaluationClientDetailsProps {
  renderType?: "client-details-page" | "report-page-component";
  onEditingDone?: () => void;
}

export const EvaluationClientDetails: React.FC<EvaluationClientDetailsProps> = ({
  renderType = "client-details-page",
  onEditingDone,
}) => {
  const { report, reportQuery } = useReport();

  const learnerId = report?.learner?.id || null;
  const go = useNavigate();
  const { pathname } = useLocation();
  const isReportPageComponent = renderType === "report-page-component";

  const addEvaluationClientDetailsMutation = useAddReportClientDetailsMutation({});
  const updateEvaluationClientDetailsMutation = useUpdateReportClientDetailsMutation({});

  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as EvaluationSubRoute) || ("upload-files" as EvaluationSubRoute);
  const nextRoute = getNextRoute(currentRoute);

  const { isPending: saveCallLoading } = addEvaluationClientDetailsMutation;
  const { isPending: updateCallLoading } = updateEvaluationClientDetailsMutation;

  const { onSubmit, formState, RegisteredFormInput, RegisteredFormSelected, reset, setValue, trigger, watch } =
    useFormUtils<UpdateReportClientDetailsMutationVariables>({
      mutationFn: async (params) => {
        if (!learnerId) {
          await addEvaluationClientDetailsMutation.mutateAsync(
            {
              reportId: report?.id ?? "<missing-evaluation-id>",
              learnerCaregiverMinimalInput: {
                ...params.learnerCaregiverMinimalInput,
                gender: setGenderValue(params.learnerCaregiverMinimalInput.gender),
              },
            },
            {
              onSuccess: async () => {
                await reportQuery.refetch();
                notifySuccess("Successfully Created Client");
                go(`../${nextRoute}`);
              },
              onError: async (error) => {
                console.error("Error creating client", error);
                notifyError("Error Creating Client");
              },
            },
          );
        } else {
          await updateEvaluationClientDetailsMutation.mutateAsync(
            {
              reportId: report?.id ?? "<missing-evaluation-id>",
              learnerCaregiverMinimalInput: {
                ...params.learnerCaregiverMinimalInput,
                gender: setGenderValue(params.learnerCaregiverMinimalInput.gender),
              },
              reportWriterAndDate: params.reportWriterAndDate,
            },
            {
              onSuccess: async () => {
                await reportQuery.refetch();
                if (!isReportPageComponent) {
                  notifySuccess("Successfully Updated Client Details");
                  go(`../${nextRoute}`);
                } else {
                  notifySuccess("Successfully Updated General Information Details");
                  onEditingDone && onEditingDone();
                }
              },
              onError: async (error) => {
                console.error("Error updating client details", error);
                notifyError("Error Updating Client Details");
              },
            },
          );
        }
      },
    });

  useEffect(() => {
    if (report) {
      const formattedGender = getGenderValue(report?.reportClientDetails?.gender) || "";
      reset({
        learnerCaregiverMinimalInput: {
          clientFirstName: report?.reportClientDetails?.clientFirstName,
          clientLastName: report?.reportClientDetails?.clientLastName || "",
          birthDate: report?.reportClientDetails?.birthDate,
          gender: formattedGender,
          specifyGender: report?.reportClientDetails?.specifyGender || "",
          address: report?.reportClientDetails?.address || "",
          addressLine2: report?.reportClientDetails?.addressLine2,
          city: report?.reportClientDetails?.city,
          state: report?.reportClientDetails?.state,
          zip: report?.reportClientDetails?.zip,
          caregiverFirstName: report?.reportClientDetails?.guardianFirstName,
          caregiverLastName: report?.reportClientDetails?.guardianLastName || "",
          caregiver2FirstName: report?.reportClientDetails?.guardian2FirstName,
          caregiver2LastName: report?.reportClientDetails?.guardian2LastName || "",
        },
        reportWriterAndDate: {
          reportCompletionDate: report?.completionDate,
          reportWriter: report?.reportWriter,
        },
      });
    } else {
      reset({ learnerCaregiverMinimalInput: {} });
    }
  }, [report]);

  const genderWatch = watch("learnerCaregiverMinimalInput.gender");

  const isReadOnly = false;
  const isPending = saveCallLoading || updateCallLoading;

  const getForm = () => (
    <>
      {!isReadOnly && !isReportPageComponent ? (
        <DevOnlyWrapper>
          <div className="my-4 flex items-center">
            <Button
              appearance="link"
              className="mr-2"
              text="Fill Fields (dev only)"
              onClick={() => {
                setValue("learnerCaregiverMinimalInput.clientFirstName", `(fake) ${faker.person.firstName()}`);
                setValue("learnerCaregiverMinimalInput.clientLastName", `(fake) ${faker.person.lastName()}`);
                setValue(`learnerCaregiverMinimalInput.birthDate`, format(faker.date.birthdate(), "yyyy-MM-dd"), {
                  shouldValidate: true,
                });
                setValue(
                  "learnerCaregiverMinimalInput.gender",
                  `${faker.helpers.arrayElement(["Boy/man", "Girl/woman"])}`,
                );
                setValue("learnerCaregiverMinimalInput.address", `${faker.location.streetAddress()}`);
                setValue("learnerCaregiverMinimalInput.addressLine2", `${faker.location.secondaryAddress()}`);
                setValue("learnerCaregiverMinimalInput.city", `${faker.location.city()}`);
                setValue("learnerCaregiverMinimalInput.state", `${faker.location.state()}`);
                setValue("learnerCaregiverMinimalInput.zip", `${faker.location.zipCode()}`);
                setValue("learnerCaregiverMinimalInput.caregiverFirstName", `(fake) ${faker.person.firstName()}`);
                setValue("learnerCaregiverMinimalInput.caregiverLastName", `(fake) ${faker.person.lastName()}`);
                setValue("learnerCaregiverMinimalInput.caregiver2FirstName", `(fake) ${faker.person.firstName()}`);
                setValue("learnerCaregiverMinimalInput.caregiver2LastName", `(fake) ${faker.person.lastName()}`);
                trigger();
              }}
            />
            <Button
              appearance="link"
              text="Clear Fields (dev only)"
              onClick={() => {
                reset();
                trigger();
              }}
            />
          </div>
        </DevOnlyWrapper>
      ) : null}
      <div className="col-span-3 flex flex-col">
        <div className="gap-x-4  lg:grid lg:grid-cols-2">
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.clientFirstName"
            inputSize="full"
            formState={formState}
            label="Client’s First Name"
            readOnly={isReadOnly}
          />
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.clientLastName"
            inputSize="full"
            formState={formState}
            label="Client's Last Name"
            readOnly={isReadOnly}
          />
        </div>
      </div>
      <div className="gap-x-4 lg:grid lg:grid-cols-1">
        <RegisteredFormInput
          formKey="learnerCaregiverMinimalInput.birthDate"
          inputSize="full"
          type="date"
          formState={formState}
          max={formatDateYYYY_MM_DD(new Date())}
          label="Date of Birth"
          readOnly={isReadOnly}
        />
        <div />
      </div>
      {isReadOnly ? (
        <DoubleColumnContainer>
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.gender"
            formState={formState}
            label="Gender"
            readOnly
            inputSize="full"
          />
          {genderWatch === "Gender identity is not listed" && (
            <RegisteredFormInput
              formKey="learnerCaregiverMinimalInput.specifyGender"
              formState={formState}
              required={false}
              label="Specify Gender"
              readOnly
              inputSize="full"
            />
          )}
        </DoubleColumnContainer>
      ) : (
        <DoubleColumnContainer>
          <RegisteredFormSelected
            formKey="learnerCaregiverMinimalInput.gender"
            formState={formState}
            placeholderText={""}
            title="Gender"
            readOnly={isReadOnly}
            items={
              Object.values(GenderApiValues).map((o) => ({
                primary: getGenderValue(o),
              })) as SelectItem[]
            }
          />
          {genderWatch === "Gender identity is not listed" && (
            <RegisteredFormInput
              formKey="learnerCaregiverMinimalInput.specifyGender"
              formState={formState}
              required={false}
              label="Specify Gender"
              readOnly={isReadOnly}
            />
          )}
        </DoubleColumnContainer>
      )}
      <div className="col-span-3 flex flex-col">
        <div className="gap-x-4  lg:grid lg:grid-cols-2">
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.address"
            inputSize="full"
            formState={formState}
            label="Address Line 1"
            required={false}
            readOnly={isReadOnly}
          />
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.addressLine2"
            inputSize="full"
            required={false}
            formState={formState}
            label="Address Line 2"
            readOnly={isReadOnly}
          />
        </div>
      </div>
      <div className="col-span-3 flex flex-col">
        <div className="gap-x-4  lg:grid lg:grid-cols-2">
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.city"
            inputSize="full"
            formState={formState}
            label="City"
            required={false}
            readOnly={isReadOnly}
          />

          <div className="gap-x-4  lg:grid lg:grid-cols-2">
            <RegisteredFormInput
              formKey="learnerCaregiverMinimalInput.state"
              inputSize="full"
              formState={formState}
              label="State / Province"
              required={false}
              readOnly={isReadOnly}
            />
            <RegisteredFormInput
              formKey="learnerCaregiverMinimalInput.zip"
              inputSize="full"
              formState={formState}
              label="ZIP / Postal Code"
              required={false}
              readOnly={isReadOnly}
            />
          </div>
        </div>
      </div>

      <div className="col-span-3 flex flex-col">
        <div className="gap-x-4  lg:grid lg:grid-cols-2">
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.caregiverFirstName"
            inputSize="full"
            formState={formState}
            label="Guardian’s First Name"
            readOnly={isReadOnly}
          />
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.caregiverLastName"
            inputSize="full"
            formState={formState}
            label="Guardian’s Last Name"
            readOnly={isReadOnly}
          />
        </div>

        <div className="gap-x-4  lg:grid lg:grid-cols-2">
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.caregiver2FirstName"
            inputSize="full"
            formState={formState}
            label="Second Guardian’s First Name"
            required={false}
            readOnly={isReadOnly}
          />
          <RegisteredFormInput
            formKey="learnerCaregiverMinimalInput.caregiver2LastName"
            inputSize="full"
            formState={formState}
            label="Second Guardian’s Last Name"
            required={false}
            readOnly={isReadOnly}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="flex w-full flex-col ">
      <FormContainer onSubmit={onSubmit}>
        {!isReportPageComponent ? (
          <>
            <ContentRow title="Client Details">{getForm()}</ContentRow>
            <FooterButtonRow
              primaryButtonTitle={"Continue"}
              primaryButtonType="submit"
              primaryButtonLoading={isPending || reportQuery.isPending}
              hideBackButton={true}
            />
          </>
        ) : (
          <div className="mt-4">
            {getForm()}
            <div className="col-span-3 flex flex-col">
              <div className="gap-x-4  lg:grid lg:grid-cols-2">
                <RegisteredFormInput
                  formKey="reportWriterAndDate.reportCompletionDate"
                  inputSize="full"
                  type="date"
                  formState={formState}
                  max={formatDateYYYY_MM_DD(new Date())}
                  label="Date of Report"
                  readOnly={isReadOnly}
                />
                <RegisteredFormInput
                  formKey="reportWriterAndDate.reportWriter"
                  inputSize="full"
                  formState={formState}
                  label="Examiner"
                  readOnly={isReadOnly}
                />
              </div>
            </div>
            <div className="flex flex-row-reverse items-right gap-2">
              <Button appearance="primary" disabled={updateCallLoading} type="submit" text="Save" />
              <Button appearance="secondary" disabled={updateCallLoading} text="Cancel" onClick={onEditingDone} />
            </div>
          </div>
        )}
      </FormContainer>
    </div>
  );
};
